import {
  Chip,
  createStyles,
  FormControl,
  IconButton,
  Link,
  makeStyles,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  Typography,
} from '@material-ui/core';
import React, { useState } from 'react';
import { commonStyles } from '../../../../../../styles/common';
import BlockIcon from '@material-ui/icons/Block';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { capitalizeFirstLetter } from '../../../../../home/allAssets/util';
import ConfirmationPopup from '../../PlannerQEBot/components/ConfirmationPopup';

interface Project {
  instanceid: string;
  statusutc: string;
  statusmsg: string;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    ...commonStyles(theme),
    table: {
      minWidth: 650,
    },
    tableHead: {
      backgroundColor: theme.palette.table.headerBackground + ' !important',
    },
    tableHeadCell: {
      color: theme.palette.text.secondary,
      fontWeight: 600,
    },
    chip: {
      backgroundColor: theme.palette.colors.lightGreen,
      padding: `${theme.spacing(0.5)}px ${theme.spacing(2)}px`,
    },
    checkIcon: {
      color: theme.palette.successText,
    },
    blockIcon: {
      color: theme.palette.errorText,
    },
    infoIcon: {
      color: theme.palette.warningText,
    },
    dropdownACFormat: {
      width: '120px',
      padding: '12px',
    },
  }),
);

const DevOpsTable = ({ isToolAdmin, projects, details, onChange }: any) => {
  const classes = useStyles();
  const projectDetails = details?.data?.[0]?.data?.[0]?.data;

  const getAcceptanceCriteriaFormat = (projectId: string) => {
    const setting = details['custom-settings']?.find(
      s => s.projectid === projectId,
    );
    return setting?.settings?.acceptanceCriteriaFormat;
  };

  enum status {
    ENABLED = 'enabled',
    WORKING = 'working',
    DISABLED = 'disabled',
  }

  interface ConfigFormat {
    value: string;
    label: string;
  }

  const emptyPopupState = {
    open: false,
    popupContent: {
      title: '',
      description: '',
      subDescription: '',
      enabled: false,
    },
    onConfirm: () => {},
    onClose: () => {},
  };

  const [confirmationPopup, setConfirmationPopup] = useState({
    ...emptyPopupState,
  });

  const handleCloseConfirmationPopup = () => {
    setConfirmationPopup({ ...emptyPopupState });
  };

  const handleConfirmEnablePopup = (project: Project) => {
    onChange(project);
    handleCloseConfirmationPopup();
  };

  const handleConfirmationPopup = (project: Project) => {
    const enabled = project?.statusmsg
      ? project?.statusmsg?.toLowerCase() === status.ENABLED
      : false;
    setConfirmationPopup({
      open: true,
      popupContent: {
        title: enabled ? 'Disable - DevOps Agent' : 'Enable - DevOps Agent',
        description: enabled
          ? 'Are you sure you want to disable this service?'
          : 'Are you sure you want to enable this service?',
        subDescription: enabled
          ? 'This feature will be disabled from the project.'
          : 'This feature will be enabled from the project.',

        enabled: enabled,
      },
      onConfirm: () => {
        handleConfirmEnablePopup(project);
      },
      onClose: () => {
        handleCloseConfirmationPopup();
      },
    });
  };

  return (
    <>
      {details && details?.data?.length > 0 ? (
        <TableContainer component={Paper}>
          <Table
            size="small"
            className={classes.table}
            aria-label="a dense table"
          >
            <TableHead>
              <TableRow classes={{ root: classes.tableHead }}>
                <TableCell>
                  <Typography
                    classes={{ root: classes.tableHeadCell }}
                    variant="subtitle2"
                  >
                    Project Name
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography
                    classes={{ root: classes.tableHeadCell }}
                    variant="subtitle2"
                  >
                    Project ID
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography
                    classes={{ root: classes.tableHeadCell }}
                    variant="subtitle2"
                  >
                    Status
                  </Typography>
                </TableCell>
                {isToolAdmin && (
                  <TableCell>
                    <Typography
                      classes={{ root: classes.tableHeadCell }}
                      variant="subtitle2"
                    >
                      Enable/Disable
                    </Typography>
                  </TableCell>
                )}
              </TableRow>
            </TableHead>

            <TableBody>
              {projectDetails?.map((project: any) => {
                const projectDetail =
                  projects?.length !== 0 &&
                  projects?.find((detail: Project) => {
                    const instanceId = detail?.instanceid || '';
                    // Try exact match on projectid first
                    if (instanceId === project?.projectid) return true;
                    // Try exact match on projectname
                    if (instanceId === project?.projectname) return true;
                    // Try case-insensitive match on projectname
                    if (
                      instanceId?.toLowerCase() ===
                      project?.projectname?.toLowerCase()
                    )
                      return true;
                    return false;
                  });

                return (
                  <TableRow key={project?.projectid}>
                    <TableCell>
                      <Typography variant="subtitle2">
                        <Link
                          href={project?.projecturl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {project?.projectname}
                        </Link>
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle2">
                        {/* {projectDetail?.projectid || instanceid} */}
                        {project?.projectid}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle2">
                        {/* {JSON.stringify(project.enabled)} */}
                        <Chip
                          classes={{ root: classes.chip }}
                          label={
                            projectDetail?.statusmsg
                              ? capitalizeFirstLetter(projectDetail?.statusmsg)
                              : capitalizeFirstLetter(status.DISABLED)
                          }
                          icon={
                            projectDetail?.statusmsg ? (
                              projectDetail?.statusmsg?.toLowerCase() ===
                              status.ENABLED ? (
                                <CheckCircleIcon
                                  classes={{ root: classes.checkIcon }}
                                />
                              ) : projectDetail?.statusmsg?.toLowerCase() ===
                                status.WORKING ? (
                                <InfoOutlinedIcon
                                  classes={{ root: classes.infoIcon }}
                                />
                              ) : (
                                <BlockIcon
                                  classes={{ root: classes.blockIcon }}
                                />
                              )
                            ) : (
                              <BlockIcon
                                classes={{ root: classes.blockIcon }}
                              />
                            )
                          }
                          // color={project.enabled ? 'primary' : 'secondary'}
                          size="small"
                        />
                      </Typography>
                    </TableCell>
                    {isToolAdmin && (
                      <TableCell>
                        <Switch
                          checked={
                            projectDetail?.statusmsg
                              ? projectDetail?.statusmsg?.toLowerCase() ===
                                status.ENABLED
                              : false
                          }
                          onClick={() =>
                            handleConfirmationPopup(
                              projectDetail === undefined
                                ? project
                                : { ...project, ...projectDetail },
                            )
                          }
                          inputProps={{ 'aria-label': 'controlled' }}
                        />
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Typography>No projects found.</Typography>
      )}
      {confirmationPopup?.open && <ConfirmationPopup {...confirmationPopup} />}
    </>
  );
};

export default DevOpsTable;
